import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

function LightBeams() {
  const group = useRef();
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event) => {
      mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };
    
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (group.current) {
      group.current.rotation.y = time * 0.05 + mouse.current.x * 0.2;
      group.current.rotation.x = mouse.current.y * 0.1;
    }
  });

  return (
    <group ref={group}>
      {/* Purple light beam */}
      <mesh position={[15, 0, -20]} rotation={[0, 0, Math.PI / 4]}>
        <coneGeometry args={[8, 40, 32, 1, true]} />
        <meshBasicMaterial 
          color="#a855f7" 
          transparent 
          opacity={0.12}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Pink light beam */}
      <mesh position={[-12, 5, -15]} rotation={[0, 0, -Math.PI / 3]}>
        <coneGeometry args={[6, 35, 32, 1, true]} />
        <meshBasicMaterial 
          color="#ec4899" 
          transparent 
          opacity={0.15}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Magenta light beam */}
      <mesh position={[0, -8, -18]} rotation={[Math.PI / 6, 0, 0]}>
        <coneGeometry args={[7, 38, 32, 1, true]} />
        <meshBasicMaterial 
          color="#d946ef" 
          transparent 
          opacity={0.1}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </group>
  );
}

function FloatingLights() {
  const light1 = useRef();
  const light2 = useRef();
  const light3 = useRef();
  const light4 = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (light1.current) {
      light1.current.position.x = Math.sin(time * 0.5) * 20;
      light1.current.position.y = Math.cos(time * 0.3) * 15;
      light1.current.intensity = 2 + Math.sin(time * 2) * 1;
    }
    
    if (light2.current) {
      light2.current.position.x = Math.cos(time * 0.4) * 18;
      light2.current.position.y = Math.sin(time * 0.6) * 12;
      light2.current.intensity = 1.8 + Math.cos(time * 1.5) * 0.8;
    }

    if (light3.current) {
      light3.current.position.x = Math.sin(time * 0.35) * 16;
      light3.current.position.y = Math.cos(time * 0.5) * 14;
      light3.current.intensity = 1.5 + Math.sin(time * 1.8) * 0.7;
    }

    if (light4.current) {
      light4.current.position.x = Math.cos(time * 0.3) * 22;
      light4.current.position.y = Math.sin(time * 0.4) * 10;
      light4.current.intensity = 1.3 + Math.cos(time * 2.2) * 0.6;
    }
  });

  return (
    <>
      <pointLight 
        ref={light1}
        position={[20, 10, 5]} 
        intensity={2} 
        color="#a855f7"
        distance={80}
        decay={2}
      />
      <pointLight 
        ref={light2}
        position={[-18, -8, 8]} 
        intensity={1.8} 
        color="#ec4899"
        distance={70}
        decay={2}
      />
      <pointLight 
        ref={light3}
        position={[0, 15, -5]} 
        intensity={1.5} 
        color="#d946ef"
        distance={75}
        decay={2}
      />
      <pointLight 
        ref={light4}
        position={[-10, -12, 10]} 
        intensity={1.3} 
        color="#f472b6"
        distance={65}
        decay={2}
      />
    </>
  );
}

function GlowOrbs() {
  const orb1 = useRef();
  const orb2 = useRef();
  const orb3 = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (orb1.current) {
      orb1.current.position.x = Math.sin(time * 0.3) * 25;
      orb1.current.position.y = Math.cos(time * 0.2) * 18;
      orb1.current.position.z = Math.sin(time * 0.15) * 10;
      orb1.current.scale.setScalar(1 + Math.sin(time * 2) * 0.2);
    }
    
    if (orb2.current) {
      orb2.current.position.x = Math.cos(time * 0.25) * 22;
      orb2.current.position.y = Math.sin(time * 0.35) * 15;
      orb2.current.position.z = Math.cos(time * 0.18) * 8;
      orb2.current.scale.setScalar(1 + Math.cos(time * 1.8) * 0.25);
    }

    if (orb3.current) {
      orb3.current.position.x = Math.sin(time * 0.28) * 20;
      orb3.current.position.y = Math.cos(time * 0.4) * 20;
      orb3.current.position.z = Math.sin(time * 0.12) * 6;
      orb3.current.scale.setScalar(1 + Math.sin(time * 2.3) * 0.3);
    }
  });

  return (
    <>
      {/* Large purple orb */}
      <mesh ref={orb1} position={[15, 10, -15]}>
        <sphereGeometry args={[4, 32, 32]} />
        <meshBasicMaterial 
          color="#a855f7" 
          transparent 
          opacity={0.2}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Medium pink orb */}
      <mesh ref={orb2} position={[-12, -5, -12]}>
        <sphereGeometry args={[3, 32, 32]} />
        <meshBasicMaterial 
          color="#ec4899" 
          transparent 
          opacity={0.25}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      {/* Small magenta orb */}
      <mesh ref={orb3} position={[0, 15, -18]}>
        <sphereGeometry args={[2.5, 32, 32]} />
        <meshBasicMaterial 
          color="#d946ef" 
          transparent 
          opacity={0.22}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </>
  );
}

function LightRings() {
  const ring1 = useRef();
  const ring2 = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (ring1.current) {
      ring1.current.rotation.z = time * 0.3;
      ring1.current.rotation.x = Math.sin(time * 0.2) * 0.3;
    }

    if (ring2.current) {
      ring2.current.rotation.z = -time * 0.25;
      ring2.current.rotation.y = Math.cos(time * 0.15) * 0.2;
    }
  });

  return (
    <>
      <mesh ref={ring1} position={[0, 0, -25]}>
        <torusGeometry args={[15, 0.3, 16, 100]} />
        <meshBasicMaterial 
          color="#a855f7" 
          transparent 
          opacity={0.15}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <mesh ref={ring2} position={[0, 0, -30]}>
        <torusGeometry args={[20, 0.25, 16, 100]} />
        <meshBasicMaterial 
          color="#ec4899" 
          transparent 
          opacity={0.12}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </>
  );
}

function AnimatedGrid() {
  const gridRef = useRef();
  
  useFrame((state) => {
    if (gridRef.current) {
      gridRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.5;
      gridRef.current.material.opacity = 0.08 + Math.sin(state.clock.elapsedTime * 0.5) * 0.02;
    }
  });
  
  return (
    <mesh ref={gridRef} position={[0, -25, -40]} rotation={[-Math.PI / 3, 0, 0]}>
      <planeGeometry args={[150, 150, 30, 30]} />
      <meshBasicMaterial 
        color="#a855f7" 
        wireframe 
        transparent 
        opacity={0.08}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

function ParticleBackground() {
  return (
    <div className="canvas-container" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: -1,
      background: '#000000'
    }}>
      <Canvas
        camera={{ position: [0, 0, 50], fov: 75 }}
        gl={{ 
          antialias: true, 
          alpha: false,
          powerPreference: "high-performance"
        }}
        dpr={[1, 2]}
      >
        <color attach="background" args={['#000000']} />
        <fog attach="fog" args={['#000000', 40, 100]} />
        
        <ambientLight intensity={0.1} />
        
        <FloatingLights />
        <LightBeams />
        <GlowOrbs />
        <LightRings />
        <AnimatedGrid />
      </Canvas>

      {/* CSS Gradient Overlays */}
      <div style={{
        position: 'absolute',
        top: '10%',
        right: '10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(168, 85, 247, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(100px)',
        pointerEvents: 'none',
        animation: 'float 25s ease-in-out infinite'
      }}></div>

      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '15%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(90px)',
        pointerEvents: 'none',
        animation: 'float 20s ease-in-out infinite reverse'
      }}></div>

      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '700px',
        height: '700px',
        background: 'radial-gradient(circle, rgba(217, 70, 239, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(110px)',
        pointerEvents: 'none',
        animation: 'pulse 18s ease-in-out infinite'
      }}></div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0);
            opacity: 0.15;
          }
          50% { 
            transform: translate(50px, -50px);
            opacity: 0.25;
          }
        }
        @keyframes pulse {
          0%, 100% { 
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.1;
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.2;
          }
        }
      `}</style>
    </div>
  );
}

export default ParticleBackground;
